import { RouterRootState } from "connected-react-router";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Description from "../../../components/description/Description";
import IOModuleCard from "../../../components/hardware/IOModuleCard";
import { IOModuleDetails } from "../../../components/hardware/IOModuleDetails";
import DeviceActions from "../../../components/hardware/deviceActions";
import DeviceEventActions from "../../../components/hardware/deviceEventActions";
import DeviceInputs from "../../../components/hardware/deviceInputs";
import DeviceParameters from "../../../components/hardware/deviceParameters";
import { Topic } from "../../../components/item-content-two/Topic";
import ItemContentTabTwo from "../../../components/item-content-two/item-content-tab_two";
import SimpleItemDetails from "../../../components/item-content/SimpleItemDetails";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { TopicContextProvider } from "../../../contexts/topic/TopicContextProvider";
import { BreadcrumbLink } from "../../../models/BreadcrumbLink";
import { ContentWrapper } from "../../../models/ContentWrapper";
import { IOModule as IOModuleModel } from "../../../models/IOModule";
import { SimpleItemProfile } from "../../../models/SimpleItemProfile";
import { TabSummary } from "../../../models/TabSummary";
import { getIOModuleById, getIOModulesNamesWithPagnation, storeIOModule } from "../../../services/ioModuleService";
import { getErrorMessage, showError, showLoadItemError } from "../../../services/utilities/ServiceError";
import { useDialog } from "../../../components/dialog/DialogContext";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { getDeviceInputTabCount } from "../../../services/deviceInputService";
import { updateTabBadgeOfImproxDevices } from "../../../utilities/tabUtils";

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/hardware';
const itemPath = '/access-control/hardware/io-module/';

const IOModule: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.eventActions), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.inputs), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.actions), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.parameters), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true }
    ];
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<SimpleItemProfile>();
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const history = useHistory();    
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);

    const updateBadgeCount = async () => {
        try {
            if(itemId) {
                const deviceInputTabCount = await getDeviceInputTabCount(itemId);                
                setTabs(updateTabBadgeOfImproxDevices(tabs, deviceInputTabCount)); // need to make sure that we update the latest tab change
            }            
        }
        catch (error) {
            showError(error, toast.warn);
        }
    }

    const tabContentList: ContentWrapper[] = [
        {
            content: <IOModuleDetails />,
        },
        {
            content: <DeviceEventActions itemId={itemId} showProvidedSelectOptions={false} />,
        },
        {
            content: <DeviceInputs itemId={itemId} />,
        },
        {
            content: <DeviceActions itemId={itemId} />,
        },
        {
            content: <DeviceParameters itemId={itemId} />,
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }];

    const topic: Topic = {
        accessor: 'devName',
        title: translate.byKey(TranslationKeys.ioModules),
        allItemsPath: allItemsPath,
        itemPath: itemPath
    }

    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[1];
        return selectedTabContent.content;
    };

    const reloadPage = async () => {
        try {
            let ioModule;
            if (itemId) {
                ioModule = await getIOModuleById(itemId);
                setSelectedItem({label: ioModule.name, value: ioModule.id.toString()})
                setCurrentItem(ioModule);
                // update the tab badge counts
                updateBadgeCount()
            }
            else {
                setTabs([initialTabs[1]]);
            }
            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: itemPath + `${itemId}`, displayName: `${ioModule?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const editIOModule = (values) => {
        const newIOModule: IOModuleModel = {
            ...currentItem,
            description: values.description,
            name: values.name,
            id: values.id,
            enabled: values.enabled,
            presetId: values.presetId,
            defaultModeId: values.defaultModeId,
            threatLevelId: values.threatLevelId,
            functionId: values.functionId,
        }

        setBusy(true);
        storeIOModule(newIOModule, false)
            .then(async (value:any) => {
                setItemId(value.id)
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.ioModuleSaved));
                setCurrentItem(newIOModule);
                reloadPage();
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                // Will need to call this when updating the Preset of the IO/Module
                updateBadgeCount();
                setBusy(false);
            });
    }

    return <TopicContextProvider itemId={itemId} dataMethod={getIOModulesNamesWithPagnation} topic={topic} selectedItem={selectedItem} >
        <ItemContentTabTwo>
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={getTabContent}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={editIOModule}
                tabs={tabs}
                initialValues={currentItem}>
                <IOModuleCard />
            </SimpleItemDetails>
        </ItemContentTabTwo>
    </TopicContextProvider>
}

export default IOModule;
